import styles from "./ListGridToggle.module.scss";
import classNames from "classnames/bind";
import { useCallback, useEffect, useRef } from "react";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useFeatureFlags } from "@/flags/flags";
import { StandardProps } from "@/types/types";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { GridIcon, ListIcon } from "./Icons";
import { dispatchAnalyticsEvent } from "@/utils/analytics";
import { PLP_VIEW_TYPE_SELECTED } from "@/utils/events";
import { LinkWithoutPrefetch } from "../__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";

const cx = classNames.bind(styles);

export function ListGridToggle({
  configProps,
}: Pick<StandardProps, "configProps">) {
  const route = useRouteParams();
  const flags = useFeatureFlags();

  const firstProductIdInView = useRef<string | undefined>(undefined);

  const queryParams = route.query;

  const isListView = queryParams.plpView
    ? queryParams.plpView === "list"
    : flags.isListViewOn.value;

  const handleToggle = useCallback(() => {
    firstProductIdInView.current = getFirstInViewProduct();
    dispatchAnalyticsEvent(PLP_VIEW_TYPE_SELECTED, {
      plpViewType: !isListView ? "list" : "grid",
    });
  }, [isListView]);

  useEffect(() => {
    if (firstProductIdInView.current) {
      scrollProductIntoView(firstProductIdInView.current);
      firstProductIdInView.current = undefined;
    }
  }, [isListView]);

  if (!flags.isListViewOn.value) {
    return null;
  }

  return (
    <LinkWithoutPrefetch
      href={{
        pathname: route.path,
        query: sanitiseQueryParams({
          ...queryParams,
          plpView: isListView ? "grid" : "list",
        }),
      }}
      data-component="ListGridToggle"
      data-testid="list-grid-toggle"
      className={cx("button", {
        "button--on": isListView,
        "button--desktop-only": configProps?.desktopOnly,
        "button--mobile-only": configProps?.mobileOnly,
      })}
      onClick={handleToggle}
      shallow
      scroll={false}
    >
      <span className={styles.buttonSection}>
        Grid <GridIcon />
      </span>
      <span className={styles.buttonSection}>
        List <ListIcon />
      </span>
    </LinkWithoutPrefetch>
  );
}

const getFirstInViewProduct = (): string | undefined => {
  const bottomOfControls =
    document.querySelector('[data-slot="sticky-bar"]')?.getBoundingClientRect()
      .bottom || 0;

  const productCards = document.querySelectorAll(
    "[data-product-card-layout][data-product-id]",
  );

  const firstCardInView = Array.from(productCards).find(
    (el) => (el as HTMLElement).getBoundingClientRect().top >= bottomOfControls,
  );

  if (firstCardInView) {
    return (firstCardInView as HTMLElement)?.dataset.productId;
  } else {
    const lastCard = productCards[productCards.length - 1];
    return (lastCard as HTMLElement)?.dataset.productId;
  }
};

const scrollProductIntoView = (productId: string) => {
  const bottomOfControls =
    document.querySelector('[data-slot="sticky-bar"]')?.getBoundingClientRect()
      .bottom || 0;

  const card = document.querySelector(
    `[data-product-card-layout][data-product-id="${productId}"]`,
  );

  window.scrollTo(
    0,
    (card?.getBoundingClientRect().top || 0) +
      window.scrollY -
      (bottomOfControls + 24),
  );
};
